/*
 * Copyright (c) 2026 GS Elektromedizinische Geraete G. Stemple GmbH, Germany - all rights reserved.
 * <p>
 * DO NOT ALTER OR REMOVE COPYRIGHT NOTICES OR THIS FILE HEADER.
 */

import React, { useContext } from 'react';
import DiviFieldCard from '../../../customs/DiviFieldCard/DiviFieldCard';
import { CardId } from '../../../../models/diviCard';
import { NodeType } from '../../../../backendModels/report.model';
import {
  BirthDateBackendFormat,
  EXTENDED_PATIENT_DATA_RECORD_ELEMENT_KEY,
  ExtendedPatientDataRecord,
  getNewBornAgeTuplesFor,
  NewBornAgeKeys,
} from '../../../../models/genericElements/extendedPatientData';
import RadioList from '../../../customs/radioList/RadioList';
import { InputState } from '../../../../backendModels/general.model';
import { useFreshCallback } from '../../../../utils/hooks';
import { Draft } from 'immer';
import { ReportsAPIContext } from '../../../../provider/ReportsAPIProvider';
import _ from 'lodash';
import { isGenericRecordDeletable } from '../../../../models/generic';
import {
  PATIENT_DATA_RECORD_ELEMENT_KEY,
  PatientDataRecord,
  PatientPropertyKeys,
} from '../../../../models/genericElements/patientData';
import dayjs from 'dayjs';
import DebouncedTextField from '../../../customs/debouncedTextField/DebouncedTextField';
import { nonEmptyString } from '../../../../utils/util';
import { ApplicationConfig } from '../../../../config/application.config';

const newBornAgeRadioItems: [NewBornAgeKeys, string][] = getNewBornAgeTuplesFor([
  NewBornAgeKeys.eineWocheAlt,
  NewBornAgeKeys.zwischenZweiUndVierWochenAlt,
]);

interface AgeProps {
  readonly nodeType: NodeType;
}

export default function Age({ nodeType }: AgeProps) {
  const { findRecordOrDefault, adaptRecord, patient, patientUpdate } = useContext(ReportsAPIContext);

  const record = findRecordOrDefault('generic', nodeType, PATIENT_DATA_RECORD_ELEMENT_KEY) as PatientDataRecord;
  const birthDate =
    record.values?.birthday?.text == null ? null : dayjs(record.values?.birthday?.text, BirthDateBackendFormat);
  const age = record?.values?.age?.number ?? null;
  const extendedRecord = findRecordOrDefault(
    'generic',
    nodeType,
    EXTENDED_PATIENT_DATA_RECORD_ELEMENT_KEY
  ) as ExtendedPatientDataRecord;
  const newbornAge = extendedRecord.values?.newbornAge?.option ?? null;

  const handleNewBornAge = useFreshCallback((value: string | null) => {
    adaptRecord(
      'generic',
      nodeType,
      (draft: Draft<ExtendedPatientDataRecord>, deleteRecord) => {
        if (value == null) {
          delete draft.values?.newbornAge;
          if (isGenericRecordDeletable(draft)) {
            deleteRecord();
          }
        } else {
          draft.inputState = InputState.ENTERED;
          draft.values = {
            ...draft.values,
            newbornAge: {
              fieldType: 'singleSelect',
              option: value as NewBornAgeKeys,
            },
          };
          if (ApplicationConfig.immediatelySyncToConference) {
            setAgeToZero();
          }
        }
      },
      EXTENDED_PATIENT_DATA_RECORD_ELEMENT_KEY
    );
    if (value != null) {
      adaptRecord(
        'generic',
        nodeType,
        (draft: Draft<PatientDataRecord>) => {
          draft.inputState = InputState.ENTERED;
          draft.values = {
            ...draft.values,
            age: {
              fieldType: 'numeric',
              number: 0,
            },
            touched: {
              options: _.union([PatientPropertyKeys.age], draft.values?.touched?.options ?? []),
              fieldType: 'multiSelect',
            },
          };
        },
        PATIENT_DATA_RECORD_ELEMENT_KEY
      );
    }
  });

  function setAgeToZero() {
    patientUpdate?.({
      ...patient,
      age: 0,
    });
  }

  const handleAgeChange = useFreshCallback((value: string) => {
    adaptRecord(
      'generic',
      nodeType,
      (draft: Draft<PatientDataRecord>, deleteRecord) => {
        draft.inputState = InputState.ENTERED;

        if (value !== (draft.values?.age?.number?.toString() ?? '')) {
          draft.values = {
            ...draft.values,
            touched: {
              options: _.union([PatientPropertyKeys.age], draft.values?.touched?.options ?? []),
              fieldType: 'multiSelect',
            },
          };
        }

        if (value == '') {
          delete draft.values?.age;
        } else {
          draft.values = {
            ...draft.values,
            age: {
              fieldType: 'numeric',
              number: Number(value),
            },
          };
          const newValue = Number(value);
          if (newValue > 0) {
            deleteNewBornAge();
          }
        }

        if (isGenericRecordDeletable(draft)) {
          deleteRecord();
        }
      },
      PATIENT_DATA_RECORD_ELEMENT_KEY
    );
  });

  function deleteNewBornAge() {
    adaptRecord(
      'generic',
      nodeType,
      (draft: Draft<ExtendedPatientDataRecord>, deleteRecord) => {
        delete draft.values?.newbornAge;
        if (isGenericRecordDeletable(draft)) {
          deleteRecord();
        }
      },
      EXTENDED_PATIENT_DATA_RECORD_ELEMENT_KEY
    );
  }

  function getTouchedClassNameAge(): string {
    const ageTouched = record.values?.touched?.options.includes(PatientPropertyKeys.age);
    return age != null && !ageTouched ? 'prefilled' : '';
  }

  function getTouchedClassNameNewBornAge(): string {
    const ageTouched = getTouchedClassNameAge();
    if (ageTouched !== '' && newbornAge !== null) {
      return ageTouched;
    }
    return '';
  }

  function hasBirthDate(): boolean {
    return birthDate != null;
  }

  function onSaveAge(valueToSave: string | undefined) {
    if (nonEmptyString(valueToSave)) {
      patientUpdate?.({ ...patient, age: Number(valueToSave) });
    } else {
      patientUpdate?.(_.omit(patient, ['age']));
    }
  }

  return (
    <DiviFieldCard cardType={CardId.Age}>
      <div className='column'>
        <DebouncedTextField
          className={getTouchedClassNameAge()}
          label='Jahre'
          type={'number'}
          value={age?.toString() ?? ''}
          slotProps={{ input: { inputProps: { step: 1, min: 0 }, disabled: hasBirthDate() } }}
          onDebounceChange={handleAgeChange}
          onUnfocus={onSaveAge}
          disabled={hasBirthDate()}
        />
        <RadioList
          className={`symptoms-list add-margin-top ${getTouchedClassNameNewBornAge()}`}
          items={newBornAgeRadioItems}
          radioProps={{ disabled: hasBirthDate() }}
          value={newbornAge}
          onChange={handleNewBornAge}
        />
      </div>
    </DiviFieldCard>
  );
}
